import type {
  Address,
  Hex,
  PublicClient,
  TestClient,
  WalletClient,
} from "viem";

/** Result of a time travel operation. */
export interface TimeResult {
  oldTimestamp: bigint;
  newTimestamp: bigint;
  blockNumber: bigint;
}

/** Result of a mine operation. */
export interface MineResult {
  startBlock: bigint;
  endBlock: bigint;
}

/** Client bundle for chain control operations. */
export interface ChainClients {
  publicClient: PublicClient;
  testClient: TestClient;
  walletClient?: WalletClient;
}

/**
 * Fast-forward chain time by a number of seconds.
 * Mines a single block after advancing time.
 */
export async function fastForward(
  clients: ChainClients,
  seconds: number,
): Promise<TimeResult> {
  const block = await clients.publicClient.getBlock();
  const oldTimestamp = block.timestamp;

  await clients.testClient.increaseTime({ seconds });
  await clients.testClient.mine({ blocks: 1 });

  const newBlock = await clients.publicClient.getBlock();
  return {
    oldTimestamp,
    newTimestamp: newBlock.timestamp,
    blockNumber: newBlock.number,
  };
}

/** Mine a specified number of blocks. */
export async function mineBlocks(
  clients: ChainClients,
  count: number,
  intervalSeconds?: number,
): Promise<MineResult> {
  const startBlock = await clients.publicClient.getBlockNumber();

  if (intervalSeconds) {
    await clients.testClient.mine({ blocks: count, interval: intervalSeconds });
  } else {
    await clients.testClient.mine({ blocks: count });
  }

  const endBlock = await clients.publicClient.getBlockNumber();
  return { startBlock, endBlock };
}

/** Set the chain timestamp to a specific value. */
export async function setTimestamp(
  clients: ChainClients,
  timestamp: bigint,
): Promise<void> {
  await clients.testClient.setNextBlockTimestamp({ timestamp });
  await clients.testClient.mine({ blocks: 1 });
}

/** Take an EVM snapshot, returning a snapshot ID. */
export async function takeSnapshot(clients: ChainClients): Promise<Hex> {
  // TestClient.snapshot() returns the snapshot id
  const id = await clients.testClient.snapshot();
  return id;
}

/** Revert to a previously taken snapshot. */
export async function revertSnapshot(
  clients: ChainClients,
  snapshotId: Hex,
): Promise<void> {
  await clients.testClient.revert({ id: snapshotId });
}

/** Set ETH balance for an address. */
export async function setEthBalance(
  clients: ChainClients,
  address: Address,
  weiAmount: bigint,
): Promise<void> {
  await clients.testClient.setBalance({ address, value: weiAmount });
}

/**
 * Set an ERC-20 token balance via storage slot manipulation.
 *
 * @param clients - Chain clients
 * @param tokenAddress - ERC-20 contract address
 * @param account - Target account
 * @param slot - balanceOf storage slot index
 * @param amount - Token amount in smallest units
 */
export async function setTokenBalance(
  clients: ChainClients,
  tokenAddress: Address,
  account: Address,
  slot: number,
  amount: bigint,
): Promise<void> {
  const { keccak256, encodePacked, pad, toHex } = await import("viem");
  // balanceOf mapping slot: keccak256(abi.encode(account, slot))
  const storageKey = keccak256(
    encodePacked(
      ["bytes32", "bytes32"],
      [pad(account, { size: 32 }), pad(toHex(BigInt(slot)), { size: 32 })],
    ),
  );

  await clients.testClient.setStorageAt({
    address: tokenAddress,
    index: storageKey,
    value: pad(toHex(amount), { size: 32 }),
  });
}

/** Impersonate an account for transaction signing. */
export async function impersonate(
  clients: ChainClients,
  address: Address,
): Promise<void> {
  await clients.testClient.impersonateAccount({ address });
}

/** Stop impersonating an account. */
export async function stopImpersonating(
  clients: ChainClients,
  address: Address,
): Promise<void> {
  await clients.testClient.stopImpersonatingAccount({ address });
}

/**
 * Dump the entire chain state.
 * Returns the state as a hex-encoded string via tevm_dumpState.
 */
export async function dumpState(clients: ChainClients): Promise<Hex> {
  const result = await (
    clients.testClient as {
      request: (args: {
        method: string;
        params?: unknown[];
      }) => Promise<unknown>;
    }
  ).request({
    method: "tevm_dumpState",
    params: [],
  });
  return result as Hex;
}

/**
 * Load a previously dumped chain state.
 */
export async function loadState(
  clients: ChainClients,
  state: Hex,
): Promise<void> {
  await (
    clients.testClient as {
      request: (args: {
        method: string;
        params?: unknown[];
      }) => Promise<unknown>;
    }
  ).request({
    method: "tevm_loadState",
    params: [state],
  });
}
